import { useState, useEffect } from "react";
import { Routes, Route, Link, useLocation } from "react-router-dom";
import { motion } from "framer-motion";
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation, Pagination, Autoplay } from 'swiper/modules';
import 'swiper/css';
import 'swiper/css/navigation';
import 'swiper/css/pagination';
import Turnstile from "react-turnstile";
import { 
  Cloud, 
  Code2, 
  Shield, 
  TrendingDown, 
  LineChart, 
  Database, 
  HeadphonesIcon,
  ArrowRight,
  Mail,
  Phone,
  Instagram,
  CheckCircle2,
  Server,
  Gauge,
  ExternalLink,
  Menu,
  X,
  Linkedin
} from "lucide-react";
import axios from "axios";
import ServicePage from "./ServicePage";
import CasesPage, { cases } from "./CasesPage";
import BlogPage from "./BlogPage";
import BlogPostPage from "./BlogPostPage";
import CaseDetailPage from "./CaseDetailPage";
import CookieBanner from "./components/CookieBanner";
import CookieManager from "./components/CookieManager";
import CookieSettingsButton from "./components/ui/CookieSettingsButton";
import { organizationSchema, usePageSeo, websiteSchema } from "./seo";

const LOGO_URL = "/logo_suanuvem.avif";
// Services data com ícones
const services = [
  {
    icon: Code2,
    title: "DevOps As a Service",
    description: "Implementação e gestão de práticas DevOps para acelerar entregas, melhorar qualidade e reduzir custos.",
    span: "md:col-span-4",
    link: "/servicos/devops-as-a-service"
  },
  {
    icon: Server,
    title: "Cloud Privada",
    description: "Soluções em cloud privada da Sua Nuvem. Flexibilidade da nuvem pública com controle total da privada.",
    span: "md:col-span-4",
    link: "/servicos/cloud-privada"
  },
  {
    icon: TrendingDown,
    title: "FinOps - Otimização de Custos",
    description: "Maximize o valor investido em nuvem com governança, visibilidade e práticas de FinOps.",
    span: "md:col-span-4",
    link: "/servicos/finops"
  },
  {
    icon: Shield,
    title: "Segurança & DevSecOps",
    description: "Segurança integrada ao ciclo de vida com controles automatizados, conformidade e hardening.",
    span: "md:col-span-4",
    link: "/servicos/seguranca-devsecops"
  },
  {
    icon: LineChart,
    title: "Observabilidade & Monitoramento",
    description: "Métricas, logs, traces, dashboards e alertas para reduzir MTTR e aumentar a estabilidade.",
    span: "md:col-span-4",
    link: "/servicos/observabilidade"
  },
  {
    icon: Database,
    title: "Backup, DR & Continuidade",
    description: "Estratégias de backup, disaster recovery e continuidade para proteger dados e serviços críticos.",
    span: "md:col-span-4",
    link: "/servicos/backup-dr"
  },
  {
    icon: HeadphonesIcon,
    title: "Suporte & Sustentação",
    description: "Operação contínua com monitoramento, gestão de incidentes, SLAs e melhoria contínua.",
    span: "md:col-span-4",
    link: "/servicos/suporte-sustentacao"
  },
];

// Sales team
const salesTeam = [
  {
    name: "Contate-nos",
    email: "comercial@suanuvem.io",
    phone: "+55 84 98638-0832",
    url: "https://api.whatsapp.com/send/?phone=5584986380832&text=Ol%C3%A1%20Tenho%20Interesse!&type=phone_number&app_absent=0"
  }
];

function HomePage() {
  usePageSeo({
    title: "Sua Nuvem | Cloud Privada, DevOps, FinOps e DevSecOps",
    description:
      "Cloud privada, DevOps as a Service, FinOps e segurança para empresas que precisam de performance, controle de custos, alta disponibilidade e suporte especializado.",
    path: "/",
    structuredData: [organizationSchema(), websiteSchema()],
  });

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    company: "",
    message: ""
  });
// Estado para o token do Turnstile
  const [turnstileToken, setTurnstileToken] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null);

  // Handle hash navigation and scroll
  useEffect(() => {
    const handleHashChange = () => {
      const hash = window.location.hash;
      if (hash) {
        const elementId = hash.substring(1); // Remove #
        const element = document.getElementById(elementId);
        if (element) {
          setTimeout(() => {
            element.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 100);
        }
      }
    };

    // Check on mount
    handleHashChange();

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);
    
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus(null);

try {
      await axios.post("/api/contact", {
        ...formData,
        turnstileToken: turnstileToken
      });
      setSubmitStatus("success");
      setFormData({ name: "", email: "", telefone: "", company: "", message: "" });
      setTurnstileToken(null);
    } catch (error) {
      console.error("Error submitting form:", error);
      setSubmitStatus("error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  return (
    <div className="bg-brand-dark text-white">
      {/* Hero Section - Estilo MyTech */}
      <section className="pt-32 md:pt-40 pb-24 md:pb-32 px-6 md:px-12 relative overflow-hidden" data-testid="hero-section">
        {/* Background decorativo */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-20 left-10 w-72 h-72 bg-brand-sage rounded-full blur-3xl"></div>
          <div className="absolute bottom-20 right-10 w-96 h-96 bg-brand-sage rounded-full blur-3xl"></div>
        </div>

        <div className="max-w-7xl mx-auto relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto"
          >
            <h1 className="text-5xl md:text-7xl font-bold tracking-tight leading-tight mb-6">
              Cloud privada e DevOps para empresas que precisam de <span className="text-brand-sage">performance,</span> <span className="text-brand-sage">segurança</span> e <span className="text-brand-sage">controle total da infraestrutura.</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 leading-relaxed mb-10">
              Implantamos, gerenciamos e protegemos sua infraestrutura com tecnologia de ponta.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="#contato"
                className="bg-brand-sage text-brand-dark px-8 py-4 rounded-md font-semibold hover:bg-opacity-90 transition-all inline-flex items-center justify-center gap-2 text-lg"
                data-testid="hero-cta-button" 
              >
                Solicite um diagnóstico gratuito
                <ArrowRight className="w-5 h-5" />
              </a>
              <a
                href="#solucoes"
                className="border-2 border-brand-sage text-brand-sage px-8 py-4 rounded-md font-semibold hover:bg-brand-sage/10 transition-all inline-flex items-center justify-center text-lg"
                data-testid="hero-services-button"
              >
                Conheça nossas soluções
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Solutions Section - Estilo MyTech */}
      <section id="solucoes" className="py-24 md:py-32 px-6 md:px-12" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight mb-6">
              Soluções em nuvem que <span className="text-brand-sage">impulsionam</span> sua empresa
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Oferecemos uma gama completa de serviços para atender às necessidades específicas da sua empresa
            </p>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
            {services.map((service, index) => {
              const Icon = service.icon;
              return (
                <motion.div
                  key={service.title}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  className={`${service.span} group`}
                  data-testid={`service-card-${index}`}
                >
                  <Link to={service.link}>
                    <div className="h-full border border-white/10 hover:border-brand-sage/50 bg-brand-surface/30 p-8 rounded-lg transition-all duration-300 hover:shadow-[0_0_40px_-10px_rgba(137,167,146,0.3)] cursor-pointer">
                      <div className="w-16 h-16 rounded-lg bg-brand-sage/10 flex items-center justify-center mb-6">
                        <Icon className="w-8 h-8 text-brand-sage" />
                      </div>
                      <h3 className="text-xl font-semibold mb-3">{service.title}</h3>
                      <p className="text-gray-400 leading-relaxed mb-4">{service.description}</p>
                      <span className="text-brand-sage text-sm font-semibold flex items-center gap-2 group-hover:gap-3 transition-all">
                        Saiba mais <ArrowRight className="w-4 h-4" />
                      </span>
                    </div>
                  </Link>
                </motion.div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Mid Section */}
      <section className="py-20 px-6 md:px-12 bg-brand-sage/10 border-y border-brand-sage/20">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Pronto para levar a sua empresa para outro nível?
            </h2>
            <p className="text-xl text-gray-300 mb-8">
              Entre em contato com nossos especialistas e descubra como a Sua Nuvem pode ajudar.
            </p>
            <a
              href="#contato"
              className="inline-block bg-brand-sage text-brand-dark px-8 py-4 rounded-md font-semibold hover:bg-opacity-90 transition-all text-lg"
            >
              Fale com um especialista agora mesmo.
            </a>
          </motion.div>
        </div>
      </section>

      {/* About Section */}
      <section id="sobre" className="py-24 md:py-32 px-6 md:px-12" data-testid="about-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <h2 className="text-4xl md:text-5xl font-bold mb-6">
                Sobre a <span className="text-brand-sage">Sua Nuvem</span>
              </h2>
              <div className="space-y-4 text-lg text-gray-300 leading-relaxed">
                <p>
                  Somos especialistas em soluções cloud, ajudamos sua empresa na jornada da nuvem com 
                  infraestrutura que garante segurança, performance e redução de custos.
                </p>
                <p>
                  Expertise comprovada em projetos de alta complexidade, da migração à operação. Atuamos de forma 
                  agnóstica em AWS, Microsoft Azure, Google Cloud, ambientes on-premises e arquiteturas híbridas/multi-cloud.
                </p>
                <p>
                  Com mais de uma década de experiência, oferecemos soluções personalizadas que vão desde 
                  consultoria estratégica até implementação completa de infraestrutura em nuvem com tecnologia 
                  proprietária da Sua Nuvem, permitindo cloud privada com flexibilidade e controle total.
                </p>
              </div>

              <div className="mt-8 grid grid-cols-2 gap-6">
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-brand-sage flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-semibold mb-1">Equipe Especializada</div>
                    <div className="text-sm text-gray-400">DevOps e Cloud experts</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-brand-sage flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-semibold mb-1">Expertise Comprovada</div>
                    <div className="text-sm text-gray-400">+10 anos de experiência</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-brand-sage flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-semibold mb-1">Suporte 24/7</div>
                    <div className="text-sm text-gray-400">Para todos os clientes</div>
                  </div>
                </div>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 text-brand-sage flex-shrink-0 mt-1" />
                  <div>
                    <div className="font-semibold mb-1">Agnóstico</div>
                    <div className="text-sm text-gray-400">Multi-cloud e híbrido</div>
                  </div>
                </div>
              </div>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="flex items-center justify-center"
            >
              <div className="relative">
                <div className="absolute inset-0 bg-brand-sage/10 blur-3xl"></div>
                <div className="relative bg-brand-surface/50 border-2 border-brand-sage/20 p-12 rounded-2xl backdrop-blur-sm">
                  <Gauge className="w-32 h-32 text-brand-sage mx-auto mb-6" />
                  <div className="text-center">
                    <div className="text-5xl font-bold text-brand-sage mb-2">99.9%</div>
                    <div className="text-gray-300 font-semibold">Uptime Garantido</div>
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Cases Section - Carrossel */}
      <section id="cases" className="py-24 md:py-32 px-6 md:px-12 bg-brand-surface/20" data-testid="cases-preview-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Resultados que comprovam nossa <span className="text-brand-sage">expertise</span>
            </h2>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Conheça algumas das empresas que confiaram na Sua Nuvem para transformar seus negócios com soluções em nuvem
            </p>
          </motion.div>

          {/* Carrossel de Cases */}
          <div className="mb-12">
            <Swiper
              modules={[Navigation, Pagination, Autoplay]}
              spaceBetween={32}
              slidesPerView={1}
              navigation
              pagination={{ clickable: true }}
              autoplay={{ delay: 5000, disableOnInteraction: false }}
              breakpoints={{
                768: {
                  slidesPerView: 2,
                },
                1024: {
                  slidesPerView: cases.length >= 3 ? 3 : cases.length,
                }
              }}
              className="cases-swiper"
            >
              {cases.map((caseItem) => (
                <SwiperSlide key={caseItem.id}>
                  <Link to={`/cases/${caseItem.id}`}>
                    <div
                      className="border border-white/10 bg-brand-dark rounded-xl overflow-hidden hover:border-brand-sage/50 transition-all duration-300 group cursor-pointer h-full"
                      data-testid={`case-preview-${caseItem.id}`}
                    >
                      <div className="p-8">
                        <div className="mb-6 h-16 flex items-center">
                          <img 
                            src={caseItem.logo} 
                            alt={caseItem.name}
                            className="h-12 w-auto object-contain"
                            width="120"
                            height="48"
                          />
                        </div>
                        <h3 className="text-2xl font-bold mb-4">{caseItem.title}</h3>
                        <p className="text-gray-400 mb-6 line-clamp-3">{caseItem.description}</p>
                        <div className="flex items-center gap-2 text-brand-sage font-semibold group-hover:gap-3 transition-all">
                          Leia mais <ArrowRight className="w-4 h-4" />
                        </div>
                      </div>
                    </div>
                  </Link>
                </SwiperSlide>
              ))}
            </Swiper>
          </div>

          <div className="text-center">
            <Link
              to="/cases"
              className="inline-block border-2 border-brand-sage text-brand-sage px-8 py-4 rounded-md font-semibold hover:bg-brand-sage/10 transition-all"
              data-testid="more-cases-link"
            >
              Mais casos de sucesso
            </Link>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contato" className="py-24 md:py-32 px-6 md:px-12" data-testid="contact-section">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="mb-16 text-center"
          >
            <h2 className="text-4xl md:text-5xl font-bold mb-4">
              Entre em contato
            </h2>
            <p className="text-xl text-gray-300 max-w-2xl mx-auto">
              Nossa equipe de especialistas está pronta para ajudar você a encontrar a solução ideal para seu negócio.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 gap-12">
            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-gray-400 mb-2">
                    Nome Completo
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    className="w-full bg-brand-surface/50 border border-gray-600 focus:border-brand-sage outline-none px-4 py-3 rounded-md text-white transition-colors"
                    data-testid="contact-form-name"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-gray-400 mb-2">
                    Email
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    className="w-full bg-brand-surface/50 border border-gray-600 focus:border-brand-sage outline-none px-4 py-3 rounded-md text-white transition-colors"
                    data-testid="contact-form-email"
                  />
                </div>
                
                <div>
                  <label htmlFor="telefone" className="block text-sm font-medium text-gray-400 mb-2">
                    Telefone
                  </label>
                  <input
                    type="tel"
                    id="telefone"
                    name="telefone"
                    value={formData.telefone}
                    onChange={handleChange}
                    required
                    className="w-full bg-brand-surface/50 border border-gray-600 focus:border-brand-sage outline-none px-4 py-3 rounded-md text-white transition-colors"
                    data-testid="contact-form-telefone"
                  />
                </div>

                <div>
                  <label htmlFor="company" className="block text-sm font-medium text-gray-400 mb-2">
                    Empresa
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    required
                    className="w-full bg-brand-surface/50 border border-gray-600 focus:border-brand-sage outline-none px-4 py-3 rounded-md text-white transition-colors"
                    data-testid="contact-form-company"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-gray-400 mb-2">
                    Mensagem
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    required
                    rows="4"
                    className="w-full bg-brand-surface/50 border border-gray-600 focus:border-brand-sage outline-none px-4 py-3 rounded-md text-white transition-colors resize-none"
                    data-testid="contact-form-message"
                  />
                </div>

                <button
                  type="submit"
                  disabled={isSubmitting || !turnstileToken}
                  className="w-full bg-brand-sage text-brand-dark px-8 py-4 rounded-md font-semibold hover:bg-opacity-90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                  data-testid="contact-form-submit"
                >
                  {isSubmitting ? "Enviando..." : !turnstileToken ? "Verificando segurança..." : "Enviar Mensagem"}
                </button>

                {/* Widget do Turnstile */}
                <div className="py-2">
                  <Turnstile
                    sitekey="0x4AAAAAACvjNVbXcKrqV-8W" // Substitua pela sua Site Key
                    onVerify={(token) => {
                      setTurnstileToken(token);
                    }}
                  />
                </div>

                {submitStatus === "success" && (
                  <div className="p-4 bg-green-500/10 border border-green-500/50 rounded-md text-green-400" data-testid="contact-form-success">
                    Mensagem enviada com sucesso! Entraremos em contato em breve.
                  </div>
                )}

                {submitStatus === "error" && (
                  <div className="p-4 bg-red-500/10 border border-red-500/50 rounded-md text-red-400" data-testid="contact-form-error">
                    Erro ao enviar mensagem. Por favor, tente novamente.
                  </div>
                )}
              </form>
            </motion.div>

            {/* Sales Team Cards */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="space-y-6"
            >
              <h3 className="text-2xl font-semibold mb-6">Fale com Nosso Time Comercial</h3>
              
              {salesTeam.map((member, index) => (
                <div
                  key={member.email}
                  className="border border-white/10 bg-brand-surface/30 p-6 rounded-lg hover:border-brand-sage/50 transition-colors"
                  data-testid={`sales-card-${index}`}
                >
                  <h4 className="text-xl font-semibold mb-1">{member.name}</h4>
                  <p className="text-gray-400 text-sm mb-4">{member.role}</p>
                  
                  <div className="space-y-3">
                    <a
                      href={`mailto:${member.email}`}
                      className="flex items-center gap-3 text-gray-300 hover:text-brand-sage transition-colors"
                      data-testid={`sales-email-${index}`}
                    >
                      <Mail className="w-5 h-5" />
                      <span className="text-sm">{member.email}</span>
                    </a>
                    
                    <a
                      href={member.url || `https://wa.me/${member.phone.replace(/\D/g, '')}`}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center gap-3 text-gray-300 hover:text-brand-sage transition-colors"
                      data-testid={`sales-phone-${index}`}
                    >
                      <Phone className="w-5 h-5" />
                      <span className="text-sm">{member.phone}</span>
                    </a>
                  </div>
                </div>
              ))}

              <div className="border border-white/10 bg-brand-surface/30 p-6 rounded-lg">
                <h4 className="text-xl font-semibold mb-4">Conecte-se Conosco</h4>
                <div className="space-y-3">
                  <a
                    href="https://instagram.com/suanuvem.io"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-300 hover:text-brand-sage transition-colors"
                    data-testid="instagram-link"
                  >
                    <Instagram className="w-5 h-5" />
                    <span className="text-sm">@suanuvem.io</span>
                  </a>
                  <a
                    href="https://www.linkedin.com/company/sua-nuvem/posts/?feedView=all"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-3 text-gray-300 hover:text-brand-sage transition-colors"
                    data-testid="linkedin-link"
                  >
                    <Linkedin className="w-5 h-5" />
                    <span className="text-sm">Sua Nuvem</span>
                  </a>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="border-t border-white/10 py-12 px-6 md:px-12 bg-brand-surface/20" data-testid="footer">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-4 gap-8 mb-8">
            <div>
              <img src={LOGO_URL} alt="Sua Nuvem" className="h-14 w-auto mb-4" width="140" height="56" />
              <p className="text-gray-400 text-sm">
                Av. Santos Dumont, 1560 – Área rural, Macaíba, RN, Brasil, CEP: 59280-000
              </p>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Soluções</h4>
              <div className="space-y-2">
                <Link to="/servicos/devops-as-a-service" className="block text-gray-400 hover:text-brand-sage transition-colors text-sm">
                  DevOps As a Service
                </Link>
                <Link to="/servicos/cloud-privada" className="block text-gray-400 hover:text-brand-sage transition-colors text-sm">
                  Cloud Privada
                </Link>
                <Link to="/servicos/finops" className="block text-gray-400 hover:text-brand-sage transition-colors text-sm">
                  FinOps
                </Link>
                <Link to="/servicos/seguranca-devsecops" className="block text-gray-400 hover:text-brand-sage transition-colors text-sm">
                  Segurança & DevSecOps
                </Link>
                <Link to="/servicos/observabilidade" className="block text-gray-400 hover:text-brand-sage transition-colors text-sm">
                  Observabilidade
                </Link>
                <Link to="/servicos/backup-dr" className="block text-gray-400 hover:text-brand-sage transition-colors text-sm">
                  Backup & DR
                </Link>
                <Link to="/servicos/suporte-sustentacao" className="block text-gray-400 hover:text-brand-sage transition-colors text-sm">
                  Suporte & Sustentação
                </Link>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Links Rápidos</h4>
              <div className="space-y-2">
                <a href="#sobre" className="block text-gray-400 hover:text-brand-sage transition-colors text-sm">
                  Sobre
                </a>
                <Link to="/cases" className="block text-gray-400 hover:text-brand-sage transition-colors text-sm">
                  Cases
                </Link>
                <Link to="/blog" className="block text-gray-400 hover:text-brand-sage transition-colors text-sm">
                  Blog
                </Link>
                <a href="#contato" className="block text-gray-400 hover:text-brand-sage transition-colors text-sm">
                  Contato
                </a>
              </div>
            </div>
            
            <div>
              <h4 className="font-semibold mb-4">Redes Sociais</h4>
              <div className="space-y-3">
                <a
                  href="https://www.instagram.com/suanuvem.io/"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-gray-400 hover:text-brand-sage transition-colors text-sm"
                  data-testid="instagram-footer"
                >
                  <Instagram className="w-5 h-5" />
                  <span>@suanuvem.io</span>
                </a>
                <a
                  href="https://www.linkedin.com/company/sua-nuvem/posts/?feedView=all"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-2 text-gray-400 hover:text-brand-sage transition-colors text-sm"
                  data-testid="linkedin-footer"
                >
                  <Linkedin className="w-5 h-5" />
                  <span>Sua Nuvem</span>
                </a>
              </div>
            </div>
          </div>
          
          <div className="border-t border-white/10 pt-8 text-center text-sm text-gray-400">
            <p>© 2026 Sua Nuvem. Todos os direitos reservados.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Header() {
  const location = useLocation();
  const isHome = location.pathname === '/';
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-brand-dark/90 border-b border-white/5">
      <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex justify-between items-center">
        <Link to="/" className="flex items-center gap-3">
          <img src={LOGO_URL} alt="Sua Nuvem" className="h-14 w-auto mb-4" width="140" height="56" />
        </Link>
        
        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center gap-8 text-sm font-medium">
          {isHome ? (
            <>
              <a href="#" className="text-gray-300 hover:text-brand-sage transition-colors">
                Início
              </a>
              <a href="#solucoes" className="text-gray-300 hover:text-brand-sage transition-colors">
                Soluções
              </a>
              <a href="#sobre" className="text-gray-300 hover:text-brand-sage transition-colors">
                Sobre
              </a>
              <a href="#cases" className="text-gray-300 hover:text-brand-sage transition-colors">
                Cases
              </a>
              <Link to="/blog" className="text-gray-300 hover:text-brand-sage transition-colors">
                Blog
              </Link>
              <a href="#contato" className="text-gray-300 hover:text-brand-sage transition-colors">
                Contato
              </a>
            </>
          ) : (
            <>
              <Link to="/" className="text-gray-300 hover:text-brand-sage transition-colors">
                Início
              </Link>
              <a href="/#solucoes" className="text-gray-300 hover:text-brand-sage transition-colors">
                Soluções
              </a>
              <a href="/#sobre" className="text-gray-300 hover:text-brand-sage transition-colors">
                Sobre
              </a>
              <a href="/#cases" className="text-gray-300 hover:text-brand-sage transition-colors">
                Cases
              </a>
              <Link to="/blog" className="text-gray-300 hover:text-brand-sage transition-colors">
                Blog
              </Link>
              <a href="/#contato" className="text-gray-300 hover:text-brand-sage transition-colors">
                Contato
              </a>
            </>
          )}
        </nav>
        
        <div className="hidden md:block">
          <button
            onClick={(e) => {
              e.preventDefault();
              window.location.replace('/#contato');
            }}
            className="bg-brand-sage text-brand-dark px-6 py-2 rounded-md font-semibold hover:bg-opacity-90 transition-all cursor-pointer"
            data-testid="header-cta-button"
          >
            Fale Conosco
          </button>
        </div>

        {/* Mobile menu button */}
        <button
          onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          className="md:hidden text-white"
          aria-label="Abrir ou fechar menu de navegação"
        >
          {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Mobile menu */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-white/10 bg-brand-dark/95 backdrop-blur-xl">
          <nav className="flex flex-col p-6 space-y-4">
            <Link to="/" className="text-gray-300 hover:text-brand-sage transition-colors" onClick={() => setMobileMenuOpen(false)}>
              Início
            </Link>
            <a href="#solucoes" className="text-gray-300 hover:text-brand-sage transition-colors" onClick={() => setMobileMenuOpen(false)}>
              Soluções
            </a>
            <a href="#sobre" className="text-gray-300 hover:text-brand-sage transition-colors" onClick={() => setMobileMenuOpen(false)}>
              Sobre
            </a>
            <a href="#cases" className="text-gray-300 hover:text-brand-sage transition-colors" onClick={() => setMobileMenuOpen(false)}>
              Cases
            </a>
            <Link to="/blog" className="text-gray-300 hover:text-brand-sage transition-colors" onClick={() => setMobileMenuOpen(false)}>
              Blog
            </Link>
            <a href="#contato" className="text-gray-300 hover:text-brand-sage transition-colors" onClick={() => setMobileMenuOpen(false)}>
              Contato
            </a>
            <button
              onClick={(e) => {
                e.preventDefault();
                setMobileMenuOpen(false);
                window.location.replace('/#contato');
              }}
              className="bg-brand-sage text-brand-dark px-6 py-3 rounded-md font-semibold hover:bg-opacity-90 transition-all text-center cursor-pointer w-full"
            >
              Fale Conosco
            </button>
          </nav>
        </div>
      )}
    </header>
  );
}

function App() {
  return (
    <div className="App bg-brand-dark text-white">
      <Header />
      <Routes>
        <Route path="/" element={<HomePage />} />
        <Route path="/servicos/:serviceId" element={<ServicePage />} />
        <Route path="/cases" element={<CasesPage />} />
        <Route path="/cases/:caseId" element={<CaseDetailPage />} />
        <Route path="/blog" element={<BlogPage />} />
        <Route path="/blog/:postId" element={<BlogPostPage />} />
      </Routes>

      <CookieBanner />
      <CookieManager />
      <CookieSettingsButton />
    </div>
  );
}

export default App;
